import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CHAT_API_BASE = '/api/chat';

const SubscriptionContext = createContext(null);

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children, currentUser }) => {
  const [subscription, setSubscription] = useState(null);
  const [limits, setLimits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${CHAT_API_BASE}/subscription.php`);
      const data = await response.json();
      if (data.success) {
        setSubscription(data.subscription);
        setLimits(data.limits);
        setUsage(data.usage);
        setIsExpired(data.subscription.is_expired);
        setIsOverLimit(data.over_limit);
        setShowPaywall(data.subscription.is_expired);
      } else {
        setError(data.message || 'Failed to load subscription status');
      }
    } catch (err) {
      setError('Failed to load subscription status');
      console.error('Subscription fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load subscription on mount
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Re-check every 5 minutes
  useEffect(() => {
    const interval = setInterval(fetchSubscriptionStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSubscriptionStatus]);

  const activateSubscription = useCallback(async (plan = 'studio', billing = 'monthly') => {
    try {
      const response = await fetch(`${CHAT_API_BASE}/subscription.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'activate',
          plan,
          billing,
          user_id: currentUser?.id,
        }),
      });
      const data = await response.json();
      if (data.success) {
        await fetchSubscriptionStatus();
        return data;
      }
      throw new Error(data.message || 'Activation failed');
    } catch (err) {
      console.error('Failed to activate subscription:', err);
      throw err;
    }
  }, [currentUser, fetchSubscriptionStatus]);

  const dismissPaywall = useCallback(() => {
    // Only allow dismissal for over-limit warnings during active trial, not when expired
    if (!isExpired) {
      setShowPaywall(false);
      setIsOverLimit(false);
    }
  }, [isExpired]);

  const value = {
    subscription,
    limits,
    usage,
    loading,
    error,
    isExpired,
    isOverLimit,
    showPaywall,
    activateSubscription,
    dismissPaywall,
    refreshStatus: fetchSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
